import { AppSettings, Project, AIProvider, CostMode, GeminiModel, LiveVoiceProvider, SearchRegion, TTSProvider, UIThemeColors, UIThemePreset } from '../types';
import { OpenRouterService } from './openRouterService';

const SETTINGS_KEY = 'bdat_settings_v4_deep';
const env = (import.meta as any).env || {};

export const DEFAULT_UI_THEME: UIThemeColors = {
  canvas: '#edf2f8',
  canvasAccent: '#dbeafe',
  surface: '#ffffff',
  surfaceAlt: '#f8fbff',
  sidebar: '#eef3fb',
  input: '#ffffff',
  border: '#d7e1ee',
  text: '#112038',
  mutedText: '#62708a',
  accent: '#315ff4',
  accentText: '#ffffff',
};

export const DEFAULT_UI_THEME_PRESETS: UIThemePreset[] = [
  {
    id: 'light-ops',
    name: 'Light Ops',
    colors: { ...DEFAULT_UI_THEME },
  },
  {
    id: 'mist-board',
    name: 'Mist Board',
    colors: {
      canvas: '#eef4f7',
      canvasAccent: '#d7e7f6',
      surface: '#fbfdff',
      surfaceAlt: '#f1f6fb',
      sidebar: '#e8f0f8',
      input: '#ffffff',
      border: '#ced9e6',
      text: '#15253d',
      mutedText: '#6a7891',
      accent: '#0f766e',
      accentText: '#ffffff',
    },
  },
  {
    id: 'sand-ledger',
    name: 'Sand Ledger',
    colors: {
      canvas: '#f5efe6',
      canvasAccent: '#e6dccd',
      surface: '#fffaf3',
      surfaceAlt: '#f6efe4',
      sidebar: '#efe4d5',
      input: '#fffdf8',
      border: '#dbcdb9',
      text: '#2b241d',
      mutedText: '#776a5d',
      accent: '#b65c2d',
      accentText: '#ffffff',
    },
  },
];

export const DEFAULT_SETTINGS: AppSettings = {
  geminiKey: env.GEMINI_API_KEY || env.VITE_GEMINI_API_KEY || env.VITE_API_KEY || '',
  openRouterKey: env.OPENROUTER_API_KEY || env.VITE_OPENROUTER_API_KEY || '',
  openAiKey: env.OPENAI_API_KEY || env.VITE_OPENAI_API_KEY || '',
  localApiKey: env.LM_STUDIO_API_KEY || env.VITE_LM_STUDIO_API_KEY || '',
  provider: AIProvider.OPENROUTER,
  costMode: CostMode.FREE_FIRST,
  searchRegion: SearchRegion.NL,
  systemPrompt: 'Jij bent BDAT, een commerciele salesassistent die feitelijk, natuurlijk en doelgericht werkt voor de opdrachtgever.',
  appRules: 'Spreek altijd Nederlands en blijf commercieel, feitelijk en doelgericht.',
  globalRules: `1. Handel altijd namens de opdrachtgever of klant, nooit namens de lead.
2. In calls stel je jezelf voor als AI-assistent en zeg je expliciet namens welk project of welke klant je belt.
3. Het doel is verkoop: een afspraak, gig, demo, deal, productkans of een juiste doorverwijzing.
4. Verzin nooit functies, eigenaarschap, samenwerkingen, intenties of interne feiten.
5. Scheid feiten van aannames: als iets niet bevestigd is, benoem dat expliciet of stel een vraag.
6. Doe nooit uitspraken alsof jij, de gebruiker of de opdrachtgever eigenaar of mede-eigenaar van de lead bent, tenzij dat expliciet bekend is.
7. Spreek kort, natuurlijk en professioneel. Geen monologen, geen AI-taal, geen toneelspel.
8. Gebruik alleen informatie uit context of bronnen die redelijk bevestigd is.`,

  defaultModel: OpenRouterService.AUTO_MODEL_ID,
  researchModel: OpenRouterService.AUTO_MODEL_ID,
  voiceModel: GeminiModel.LIVE,
  thinkingBudget: 16000,
  openAiRealtimeModel: 'gpt-realtime',
  openAiRealtimeVoice: 'marin',
  openAiTranscriptionModel: 'gpt-4o-mini-transcribe',
  audioInputDeviceId: '',
  elevenLabsAgentId: 'agent_4901m54y75eqffn99kpjzxbdtkyk',

  autoSpeak: false,
  liveVoiceProvider: LiveVoiceProvider.ELEVENLABS,
  voiceName: 'Zephyr',
  voiceMood: 'Professional',
  voiceSpeed: 'Normal',
  theme: 'light',
  language: 'nl',
  customPresets: [],
  uiTheme: { ...DEFAULT_UI_THEME },
  uiThemePresets: [...DEFAULT_UI_THEME_PRESETS],

  appMode: 'test',
  testEmailAddress: '',
  localUrl: 'http://localhost:11434',
  ttsProvider: TTSProvider.ELEVENLABS,
  disableCallAgent: false
};

const GEMINI_TEXT_MODELS = new Set<string>([
  GeminiModel.FLASH_3,
  GeminiModel.PRO_3,
  GeminiModel.LITE_3,
  GeminiModel.THINKING_3,
]);

const GEMINI_LIVE_MODELS = new Set<string>([GeminiModel.LIVE]);
const GEMINI_DEFAULT_CHAT_MODEL = GeminiModel.FLASH_4;
const GEMINI_DEFAULT_RESEARCH_MODEL = GeminiModel.PRO_4;
const GEMINI_DEFAULT_VOICE_MODEL = GeminiModel.LIVE;

const LEGACY_GEMINI_MODEL_MAP: Record<string, string> = {
  // Oude 2.5-generatie automatisch naar de nieuwste modellen tillen.
  'gemini-2.5-flash': GeminiModel.FLASH_4,
  'gemini-2.5-pro': GeminiModel.PRO_4,
  'gemini-2.5-flash-lite': GeminiModel.LITE_4,
  'gemini-2.5-flash-preview-09-2025': GeminiModel.THINKING_3,
  'gemini-3-flash': GeminiModel.FLASH_3,
  'gemini-3-pro': GeminiModel.PRO_3,
  'gemini-3-lite': GeminiModel.LITE_3,
  'gemini-3-thinking': GeminiModel.THINKING_3,
  'gemini-3-flash-preview': GeminiModel.FLASH_3,
  'gemini-2.0-flash-001': GeminiModel.FLASH_3,
  'gemini-2.0-pro-exp-02-05': GeminiModel.PRO_3,
  'gemini-2.0-flash-lite-preview-02-05': GeminiModel.LITE_3,
  'gemini-2.0-flash-thinking-exp-01-21': GeminiModel.THINKING_3,
  'gemini-2.0-flash-exp': GeminiModel.LIVE,
  [GeminiModel.FLASH_1_5]: GeminiModel.FLASH_3,
  [GeminiModel.PRO_1_5]: GeminiModel.PRO_3,
};

const LEGACY_SYSTEM_PROMPT_PATTERNS = [/BIDAD/i, /senior specialist/i];
const LEGACY_APP_RULES = new Set<string>(['Spreek altijd Nederlands.']);
const LEGACY_GLOBAL_RULE_PATTERNS = [
  /Jij bent de beller en niet andersom/i,
  /Hallo, ik ben van \[Projectnaam\/Naam\]/i,
  /Google-afkomst/i,
];

const stripModelsPrefix = (modelId: string) =>
  modelId.startsWith('models/') ? modelId.replace('models/', '') : modelId;

const normalizeUiTheme = (theme?: Partial<UIThemeColors>): UIThemeColors => ({
  ...DEFAULT_UI_THEME,
  ...(theme || {}),
});

const normalizeUiThemePresets = (presets?: UIThemePreset[]): UIThemePreset[] => {
  const merged = [...DEFAULT_UI_THEME_PRESETS];
  for (const preset of presets || []) {
    if (!preset?.id || !preset?.name) continue;
    if (DEFAULT_UI_THEME_PRESETS.some((defaultPreset) => defaultPreset.id === preset.id)) continue;
    merged.push({
      id: preset.id,
      name: preset.name,
      colors: normalizeUiTheme(preset.colors),
    });
  }
  return merged;
};

const normalizeGeminiModel = (modelId: string, allowed: Set<string>, fallback: string) => {
  const stripped = stripModelsPrefix((modelId || '').trim());

  if (!stripped || stripped.includes('/') || stripped.includes('\\') || stripped.includes(':')) {
    return fallback;
  }

  const mapped = LEGACY_GEMINI_MODEL_MAP[stripped] ?? stripped;
  return allowed.has(mapped) ? mapped : fallback;
};

const shouldUseOpenRouterAutoModel = (modelId?: string) => {
  const normalized = (modelId || '').trim();
  return (
    !normalized ||
    normalized === OpenRouterService.AUTO_MODEL_ID ||
    normalized === OpenRouterService.PAID_AUTO_MODEL_ID ||
    GEMINI_TEXT_MODELS.has(normalized as GeminiModel)
  );
};

export const normalizeSettings = (settings?: Partial<AppSettings>): AppSettings => {
  const merged: AppSettings = { ...DEFAULT_SETTINGS, ...(settings || {}) };

  if (!Object.values(CostMode).includes(merged.costMode)) {
    merged.costMode = DEFAULT_SETTINGS.costMode;
  }

  if (!Object.values(SearchRegion).includes(merged.searchRegion)) {
    merged.searchRegion = DEFAULT_SETTINGS.searchRegion;
  }

  if (!merged.geminiKey) {
    merged.geminiKey = DEFAULT_SETTINGS.geminiKey;
  }

  if (!merged.openRouterKey) {
    merged.openRouterKey = DEFAULT_SETTINGS.openRouterKey;
  }

  if (!merged.openAiKey) {
    merged.openAiKey = DEFAULT_SETTINGS.openAiKey;
  }

  if (!merged.liveVoiceProvider) {
    merged.liveVoiceProvider = DEFAULT_SETTINGS.liveVoiceProvider;
  }

  if (!Object.values(TTSProvider).includes(merged.ttsProvider)) {
    merged.ttsProvider = DEFAULT_SETTINGS.ttsProvider;
  }

  if (!merged.openAiRealtimeModel) {
    merged.openAiRealtimeModel = DEFAULT_SETTINGS.openAiRealtimeModel;
  }

  if (!merged.openAiRealtimeVoice) {
    merged.openAiRealtimeVoice = DEFAULT_SETTINGS.openAiRealtimeVoice;
  }

  if (!merged.openAiTranscriptionModel) {
    merged.openAiTranscriptionModel = DEFAULT_SETTINGS.openAiTranscriptionModel;
  }

  if (!merged.audioInputDeviceId) {
    merged.audioInputDeviceId = DEFAULT_SETTINGS.audioInputDeviceId;
  }

  if (!merged.elevenLabsAgentId) {
    merged.elevenLabsAgentId = DEFAULT_SETTINGS.elevenLabsAgentId;
  }

  // Verwijderde voice-engines (Kokoro, browser) vallen terug op ElevenLabs.
  if (merged.ttsProvider !== TTSProvider.GEMINI && merged.ttsProvider !== TTSProvider.ELEVENLABS && merged.ttsProvider !== TTSProvider.OPENAI) {
    merged.ttsProvider = TTSProvider.ELEVENLABS;
  }

  // Classic ElevenLabs -> Realtime conversation agent, zodra er een agent-ID is.
  if (merged.liveVoiceProvider === LiveVoiceProvider.ELEVENLABS && merged.elevenLabsAgentId) {
    merged.liveVoiceProvider = LiveVoiceProvider.ELEVENLABS_REALTIME;
  }

  if (LEGACY_SYSTEM_PROMPT_PATTERNS.some((pattern) => pattern.test(merged.systemPrompt || ''))) {
    merged.systemPrompt = DEFAULT_SETTINGS.systemPrompt;
  }

  if (LEGACY_APP_RULES.has((merged.appRules || '').trim())) {
    merged.appRules = DEFAULT_SETTINGS.appRules;
  }

  if (LEGACY_GLOBAL_RULE_PATTERNS.some((pattern) => pattern.test(merged.globalRules || ''))) {
    merged.globalRules = DEFAULT_SETTINGS.globalRules;
  }

  if (merged.provider === AIProvider.GEMINI) {
    merged.defaultModel = normalizeGeminiModel(merged.defaultModel, GEMINI_TEXT_MODELS, GEMINI_DEFAULT_CHAT_MODEL);
    merged.researchModel = normalizeGeminiModel(merged.researchModel, GEMINI_TEXT_MODELS, GEMINI_DEFAULT_RESEARCH_MODEL);
    merged.voiceModel = normalizeGeminiModel(merged.voiceModel, GEMINI_LIVE_MODELS, GEMINI_DEFAULT_VOICE_MODEL);
  }

  if (merged.provider === AIProvider.OPENROUTER) {
    if (merged.costMode === CostMode.FREE_FIRST && !OpenRouterService.isFreeModel(merged.defaultModel)) {
      merged.defaultModel = OpenRouterService.AUTO_MODEL_ID;
    }

    if (merged.costMode === CostMode.FREE_FIRST && !OpenRouterService.isFreeModel(merged.researchModel)) {
      merged.researchModel = OpenRouterService.AUTO_MODEL_ID;
    }

    if (shouldUseOpenRouterAutoModel(merged.defaultModel)) {
      merged.defaultModel = merged.costMode === CostMode.BEST_QUALITY
        ? OpenRouterService.PAID_AUTO_MODEL_ID
        : OpenRouterService.AUTO_MODEL_ID;
    }

    if (shouldUseOpenRouterAutoModel(merged.researchModel)) {
      merged.researchModel = merged.costMode === CostMode.BEST_QUALITY
        ? OpenRouterService.PAID_AUTO_MODEL_ID
        : OpenRouterService.AUTO_MODEL_ID;
    }
  }

  merged.uiTheme = normalizeUiTheme(merged.uiTheme);
  merged.uiThemePresets = normalizeUiThemePresets(merged.uiThemePresets);

  merged.disableCallAgent = !!merged.disableCallAgent;

  return merged;
};

export const applyUiTheme = (theme?: Partial<UIThemeColors>) => {
  if (typeof document === 'undefined') return;

  const nextTheme = normalizeUiTheme(theme);
  const root = document.documentElement;

  root.style.setProperty('--bdat-canvas', nextTheme.canvas);
  root.style.setProperty('--bdat-canvas-accent', nextTheme.canvasAccent);
  root.style.setProperty('--bdat-surface', nextTheme.surface);
  root.style.setProperty('--bdat-surface-alt', nextTheme.surfaceAlt);
  root.style.setProperty('--bdat-sidebar', nextTheme.sidebar);
  root.style.setProperty('--bdat-input', nextTheme.input);
  root.style.setProperty('--bdat-border', nextTheme.border);
  root.style.setProperty('--bdat-text', nextTheme.text);
  root.style.setProperty('--bdat-muted', nextTheme.mutedText);
  root.style.setProperty('--bdat-accent', nextTheme.accent);
  root.style.setProperty('--bdat-accent-text', nextTheme.accentText);
};

export class SettingsService {
  static getSettings(): AppSettings {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (!saved) return DEFAULT_SETTINGS;
    try {
      const normalized = normalizeSettings(JSON.parse(saved));
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(normalized));
      return normalized;
    } catch {
      return DEFAULT_SETTINGS;
    }
  }

  static saveSettings(settings: AppSettings) {
    const normalized = normalizeSettings(settings);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(normalized));
    applyUiTheme(normalized.uiTheme);
  }

  static exportDatabase(projects: Project[]) {
    const dataStr = JSON.stringify(projects, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `bdat_backup_${new Date().toISOString().slice(0, 10)}.json`);
    linkElement.click();
  }
}
